<script lang="ts" setup>
import { computed, inject } from 'vue';
import usePortfolio from '../usePortfolio';
import { shortAddress } from '@/helpers/shortAddress';
import { useAddressBookStore } from '@/store/addressBook';
import { demoAddresses } from '@/constants';
import { copyToClipboard } from '@/helpers/copyToClipboard';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useIntervalCompute } from '@/helpers/useIntervalCompute';

dayjs.extend(relativeTime);

const addressBookStore = useAddressBookStore();

const { addressesInfo, refetch, isLoading, dataUpdatedAt } = inject(
  'portfolioData',
) as ReturnType<typeof usePortfolio>;

const getBundleName = (addresses: string[]) => {
  const bundle = addressBookStore.bundles.find((bundle) =>
    bundle.items.every((item) => addresses.includes(item.address)),
  );
  return bundle?.name;
};

const getAddressName = (address: string) => {
  const bundle = addressBookStore.items.find(
    (item) => item.address === address,
  );
  return bundle?.name;
};

const isDemo = computed(() => {
  return addressesInfo.every((item) => demoAddresses.includes(item.address));
});

const canCopy = computed(() => {
  if (addressesInfo.length === 1) {
    return {
      active: true,
      address: addressesInfo[0].address,
    };
  }
  return {
    active: false,
    address: '',
  };
});

const name = computed(() => {
  if (addressesInfo.length === 1) {
    return (
      getAddressName(addressesInfo[0].address) ??
      shortAddress(addressesInfo[0].address)
    );
  } else {
    if (isDemo.value) {
      return 'Jupiter demo';
    }
    return (
      getBundleName(addressesInfo.map((item) => item.address)) ??
      `${addressesInfo.length} wallets`
    );
  }
});

const { computedValue: lastUpdate } = useIntervalCompute({
  computeFn: () => {
    return dayjs(dataUpdatedAt.value ?? Date.now()).fromNow();
  },
  updateInterval: 30000,
});

const { computedValue: canRefresh } = useIntervalCompute({
  computeFn: () => {
    const ellapsed = dayjs().diff(dataUpdatedAt.value, 'seconds');
    return ellapsed > 30;
  },
  updateInterval: 10000,
});

const buttonDisabled = computed(() => isLoading.value || !canRefresh.value);
</script>

<template>
  <div>
    <div class="flex flex-row items-center gap-2">
      <h1
        class="text-2xl font-bold flex flex-row gap-2 items-center text-white"
      >
        <span>Portfolio</span>
        <span class="opacity-50 text-xl text-white">→</span>
        <button
          v-if="canCopy.active"
          @click="copyToClipboard(canCopy.address, 'Copied to clipboard')"
        >
          {{ name }}
        </button>
        <span v-else> {{ name }}</span>
      </h1>
      <v-tooltip
        text="Please wait 30 seconds between refreshes"
        :disabled="canRefresh"
      >
        <template #activator="{ props }">
          <div v-bind="props">
            <v-btn @click="refetch" :disabled="buttonDisabled" variant="plain">
              <v-icon
                icon="mdi-refresh"
                :class="{ 'animate-spin': isLoading }"
              ></v-icon>
            </v-btn>
          </div>
        </template>
      </v-tooltip>
    </div>
    <div class="text-xs text-disabled">
      <p :class="{ ' opacity-0': isLoading }">Last refresh {{ lastUpdate }}</p>
    </div>
  </div>
</template>
